import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { AsyncState } from '../types';
import { CodeRequestType, VisitInfo } from 'src/services/auth';
import { PatientHasDetails, patientService } from 'src/services/patient';
import { queryStringOrSessionStorage } from 'src/utils/queryStringOrSessionStorage';

type PaymentStatus = 'NOT_STARTED' | 'STARTED' | 'COMPLETED';

export type BookingFlowState = {
    steps: {
        current: number;
        max: number;
    };
    codeRequestType?: CodeRequestType;
    patientName: string;
    patientDob: string;
    sawPrepaymentMessage: boolean;
    paymentStatus: PaymentStatus;
    patientHasDetails: AsyncState<number, PatientHasDetails>;
    visitInfo?: VisitInfo;
    confirmNotifyFailed?: boolean;
};

export const fetchPatientHasDetails = createAsyncThunk('bookingFlow/fetchPatientHasDetails', async (siteId: number) => {
    if (siteId) {
        return await patientService.PatientDetails(siteId);
    }
});

const initialState: BookingFlowState = {
    steps: {
        current: 0,
        max: 5,
    },
    patientName: queryStringOrSessionStorage('patientName')!,
    patientDob: queryStringOrSessionStorage('patientDob')!,
    codeRequestType: (sessionStorage.getItem('codeRequestType') as CodeRequestType) ?? undefined,
    sawPrepaymentMessage: false,
    paymentStatus: (sessionStorage.getItem('paymentStatus') as PaymentStatus) ?? 'NOT_STARTED',
    patientHasDetails: { isLoading: false },
    visitInfo: queryStringOrSessionStorage('visit.isNewPatient')
        ? {
              isNewPatient: queryStringOrSessionStorage('visit.isNewPatient') === 'true',
              bannedLevel: queryStringOrSessionStorage('visit.bannedLevel'),
              lastF2FDate: queryStringOrSessionStorage('visit.lastF2FDate'),
              lastSeenDid: Number(queryStringOrSessionStorage('visit.lastSeenDid')) || undefined,
          }
        : undefined,
};

export const bookingFlowSlice = createSlice({
    name: 'bookingFlow',
    initialState: initialState,
    reducers: {
        setStep: (state, action: PayloadAction<number>) => {
            state.steps.current = action.payload;
        },
        setPatientName: (state, action: PayloadAction<string>) => {
            state.patientName = action.payload;
            sessionStorage.setItem('patientName', action.payload);
        },
        setPatientDob: (state, action: PayloadAction<string>) => {
            state.patientDob = action.payload;
            sessionStorage.setItem('patientDob', action.payload);
        },
        setCodeRequestType: (state, action: PayloadAction<CodeRequestType>) => {
            state.codeRequestType = action.payload;
            sessionStorage.setItem('codeRequestType', action.payload);
        },
        setSawPrepaymentMessage: (state, action: PayloadAction<boolean>) => {
            state.sawPrepaymentMessage = action.payload;
        },
        setPaymentStatus: (state, action: PayloadAction<PaymentStatus>) => {
            sessionStorage.setItem('paymentStatus', action.payload);
            state.paymentStatus = action.payload;
        },
        setConfirmNotifyFailed: (state, action: PayloadAction<boolean>) => {
            state.confirmNotifyFailed = action.payload;
        },
        resetPatientHasDetails: (state) => {
            state.patientHasDetails = { isLoading: false };
        },
    },
    extraReducers(builder) {
        builder.addCase(fetchPatientHasDetails.pending, (state, action) => {
            state.patientHasDetails = {
                isLoading: true,
                request: action.meta.arg,
            };
        });
        builder.addCase(fetchPatientHasDetails.fulfilled, (state, action) => {
            state.patientHasDetails = {
                isLoading: false,
                request: action.meta.arg,
                data: action.payload,
            };
        });
        builder.addCase(fetchPatientHasDetails.rejected, (state, action) => {
            state.patientHasDetails = {
                isLoading: false,
                request: action.meta.arg,
                error: action.error,
            };
        });
    },
});

// Action creators are generated for each case reducer function
export const {
    setStep,
    setPatientDob,
    setPatientName,
    setCodeRequestType,
    setSawPrepaymentMessage,
    setPaymentStatus,
    setConfirmNotifyFailed,
    resetPatientHasDetails,
} = bookingFlowSlice.actions;

export default bookingFlowSlice.reducer;
